/**
 * Main hook for managing the parsing workflow state
 * Handles state management, API calls, and step navigation
 */

import { useState, useCallback, useEffect } from 'react'
import { message } from 'antd'
import type { FormInstance } from 'antd'
import api from '@/services/api'
import { useParsingStore } from '@/stores/parsingStore'
import type {
  DomainWithAnalysis,
} from '../types'
import { STEP_INDEX } from '../constants'
import { transformUrls, formatDomainsFromResult } from '../utils/parsingHelpers'
import { useDomainSelection } from './useDomainSelection'
import { useDataLoaders } from './useDataLoaders'
import { useUploadFlow } from './useUploadFlow'
import { useAIAnalysisFlow } from './useAIAnalysisFlow'
import { useParsingFlow } from './useParsingFlow'
import { useWorkflowState } from './useWorkflowState'

interface UseParsingWorkflowProps {
  projectId?: number | null
  projectCrmId?: number | null
  projectName?: string | null
  credentialsId?: number | null
  initialTaskId?: string | null
  onClose: () => void
  form?: FormInstance
}

export function useParsingWorkflow({
  projectId,
  projectCrmId,
  projectName,
  credentialsId,
  initialTaskId,
  onClose,
  form,
}: UseParsingWorkflowProps) {
  // * Workflow state — managed by useReducer (Phase 3 refactor)
  // * Owns: navigation, lifecycle, parser settings, manual/input aux,
  // *       selection (crmId/queries/regions), results UI, serverWorkflow.
  // * Excluded (still useState below): `domains`, `selectedPhones`.
  const {
    state: workflowState,
    setStep: setCurrentStep,
    setInputMethod,
    setManualUrlsText,
    setManualSubsource,
    setProcessingManualUrls,
    setDownloadAll,
    setMinClicks,
    setTopQueries,
    setSelectedCrmId,
    setSelectedQueries,
    setSelectedRegionCodes,
    setResultsTab,
    setShowAllDomains,
    resetWorkflow,
    resetWorkflowKeepProject,
    fetchServerWorkflow,
  } = useWorkflowState()
  const {
    currentStep,
    inputMethod,
    serverWorkflow,
    manualUrlsText,
    manualSubsource,
    processingManualUrls,
    downloadAll,
    minClicks,
    topQueries,
    selectedCrmId,
    selectedQueries,
    selectedRegionCodes,
    resultsTab,
    showAllDomains,
  } = workflowState

  // * Project and parsing state
  // projects, loadingProjects, autoCredentialsId,
  // queriesRegions, loadingQueriesRegions, resolvedRegions,
  // aiConfig, loadingAIConfig — moved into useDataLoaders (Phase 2.2)

  // * Parsing state — moved into useParsingFlow (Phase 2.5)
  // taskId, progress, statusMessage, parsingResult, parsingFailed,
  // parsingPollRef live in useParsingFlow.

  // * Domains list (excluded from reducer — passed by ref into many sub-hooks)
  const [domains, setDomains] = useState<DomainWithAnalysis[]>([])
  // aiConfig, loadingAIConfig — moved into useDataLoaders (Phase 2.2)
  // analyzing, aiAnalysisSkipped, batchProgress, queueInfo, activeBatchId,
  // aiFilter, aiSearchText — moved into useAIAnalysisFlow (Phase 2.4)

  // * Upload state — moved into useUploadFlow (Phase 2.3)

  // * AI Analysis filters — moved into useAIAnalysisFlow (Phase 2.4)

  // * Selected phones state (excluded from reducer — Set<string>, mutates frequently)
  const [selectedPhones, setSelectedPhones] = useState<Set<string>>(new Set())

  // * activeBatchId, queueInfo — moved into useAIAnalysisFlow (Phase 2.4)

  // * Refs for polling intervals
  // parsingPollRef — moved into useParsingFlow (Phase 2.5)
  // analysisPollRef — moved into useAIAnalysisFlow (Phase 2.4)

  // * Parsing store
  const setActiveTask = useParsingStore((state) => state.setActiveTask)
  const updateTaskProgress = useParsingStore((state) => state.updateTaskProgress)
  const updateTaskStatus = useParsingStore((state) => state.updateTaskStatus)
  const completeTask = useParsingStore((state) => state.completeTask)
  const clearTask = useParsingStore((state) => state.clearTask)
  const getActiveTask = useParsingStore((state) => state.getActiveTask)

  // * Manual workflow persistence
  const saveManualWorkflow = useParsingStore((state) => state.saveManualWorkflow)
  const getManualWorkflow = useParsingStore((state) => state.getManualWorkflow)
  const clearManualWorkflow = useParsingStore((state) => state.clearManualWorkflow)

  // * AI Analysis batch persistence
  const setActiveAnalysis = useParsingStore((state) => state.setActiveAnalysis)
  const updateAnalysisProgress = useParsingStore((state) => state.updateAnalysisProgress)
  const clearActiveAnalysis = useParsingStore((state) => state.clearActiveAnalysis)
  const getActiveAnalysis = useParsingStore((state) => state.getActiveAnalysis)

  // * Domain selection sub-hook (Phase 2 refactor)
  // Provides: searchText, filteredDomains, selectedCount,
  //           toggleDomainSelection, removeDomain, saveSourceSelections
  const {
    searchText,
    setSearchText,
    filteredDomains,
    selectedCount,
    toggleDomainSelection,
    removeDomain,
    saveSourceSelections,
  } = useDomainSelection({ domains, setDomains, projectId })

  // * Data loaders sub-hook (Phase 2.2 refactor)
  // Owns: projects, queriesRegions, resolvedRegions, autoCredentialsId, aiConfig
  // Provides: loadProjects, loadQueriesRegions, loadActiveCredentials, loadAIConfig, resetData
  const {
    projects,
    loadingProjects,
    queriesRegions,
    loadingQueriesRegions,
    resolvedRegions,
    autoCredentialsId,
    aiConfig,
    loadingAIConfig,
    setAIConfig,
    loadProjects,
    loadQueriesRegions,
    loadActiveCredentials,
    loadAIConfig,
    resetData: resetDataLoaders,
  } = useDataLoaders({
    projectId,
    projectCrmId,
    projectName,
    onQueriesRegionsLoaded: useCallback(
      ({ queries, resolvedRegionCodes }: { queries: string[]; resolvedRegionCodes: string[] }) => {
        if (queries.length > 0) setSelectedQueries(queries)
        if (resolvedRegionCodes.length > 0) setSelectedRegionCodes(resolvedRegionCodes)
      },
      [setSelectedQueries, setSelectedRegionCodes]
    ),
  })

  // * Parsing flow sub-hook (Phase 2.5 refactor)
  // Owns: taskId, progress, statusMessage, parsingResult, parsingFailed, parsingPollRef
  // Provides: pollStatus, handleStartParsing, stopParsingPoll, resetParsing
  const {
    taskId,
    progress,
    statusMessage,
    parsingResult,
    parsingFailed,
    setTaskId,
    setProgress,
    setStatusMessage,
    setParsingResult,
    setParsingFailed,
    pollStatus,
    handleStartParsing,
    stopParsingPoll,
    resetParsing,
  } = useParsingFlow({
    projectId,
    projectCrmId,
    projectName,
    credentialsId,
    selectedCrmId,
    selectedQueries,
    selectedRegionCodes,
    queriesRegions,
    resolvedRegions,
    minClicks,
    downloadAll,
    topQueries,
    projects,
    autoCredentialsId,
    setDomains,
    setCurrentStep,
    updateTaskProgress,
    completeTask,
    clearTask,
    setActiveTask,
  })

  // * AI analysis flow sub-hook (Phase 2.4 refactor)
  // Owns: analyzing, batchProgress, queueInfo, activeBatchId,
  //       aiAnalysisSkipped, aiFilter, aiSearchText, analysisPollRef
  // Provides: handleAnalyze, resumeAnalysisPoll, stopAnalysisPoll, resetAnalysis
  const {
    analyzing,
    batchProgress,
    queueInfo,
    activeBatchId,
    aiAnalysisSkipped,
    aiFilter,
    aiSearchText,
    setAnalyzing,
    setActiveBatchId,
    setAiAnalysisSkipped,
    setAiFilter,
    setAiSearchText,
    handleAnalyze,
    resumeAnalysisPoll,
    stopAnalysisPoll,
    resetAnalysis,
  } = useAIAnalysisFlow({
    projectId,
    projectCrmId,
    projectName,
    domains,
    setDomains,
    currentStep,
    taskId,
    aiConfig,
    selectedPhones,
    setActiveAnalysis,
    saveManualWorkflow,
    updateAnalysisProgress,
    clearActiveAnalysis,
  })

  // * Upload flow sub-hook (Phase 2.3 refactor)
  // Owns: uploading, uploadProgress, uploadResults, localDbResults
  // Provides: handleUpload, resetUpload
  const {
    uploading,
    uploadProgress,
    uploadResults,
    localDbResults,
    setUploading,
    setUploadProgress,
    setUploadResults,
    setLocalDbResults,
    handleUpload,
    resetUpload,
  } = useUploadFlow({
    projectId,
    projectCrmId,
    domains,
    taskId,
    activeBatchId,
    manualSubsource,
    setCurrentStep,
    updateTaskStatus,
    clearManualWorkflow,
    clearActiveAnalysis,
  })

  // * Reset state
  const resetState = useCallback(() => {
    console.log('🧹 Resetting modal state...')

    // Polling timers cleared via resetParsing() / resetAnalysis() below

    // * Reducer-managed state (navigation, parser settings, selection, etc.)
    resetWorkflow()

    // * Sub-hook state and excluded useState fields
    resetParsing()
    setDomains([])
    setSearchText('')
    resetUpload()
    resetAnalysis(false)
    setSelectedPhones(new Set())
    resetDataLoaders()

    form?.resetFields()

    if (projectId && parsingFailed) {
      clearTask(projectId)
    }
  }, [projectId, parsingFailed, clearTask, form, resetDataLoaders, resetUpload, resetAnalysis, resetParsing, setSearchText, resetWorkflow])

  // * Start over - clear everything and go back to step 0
  const handleStartOver = useCallback(() => {
    console.log('🔄 Starting over - clearing all state...')

    // Polling timers cleared via resetParsing() / resetAnalysis() below

    // Clear all store data for this project
    if (projectId) {
      clearTask(projectId)
      clearManualWorkflow(projectId)
      clearActiveAnalysis(projectId)
    }

    // * Reducer-managed state (navigation, parser settings, selection, etc.)
    // * Keep selectedCrmId pointing at the current project so user doesn't
    // * re-pick the project from the dropdown after Start Over.
    resetWorkflowKeepProject(projectCrmId || null)

    // * Sub-hook state and excluded useState fields
    resetParsing()
    setDomains([])
    setSearchText('')
    resetUpload()
    resetAnalysis(true) // also clear filters
    setSelectedPhones(new Set())
    resetDataLoaders()

    form?.resetFields()

    // Set project in form if available (data will load when user proceeds to config step)
    if (projectId && projectCrmId) {
      form?.setFieldsValue({ project_id: projectId })
    }

    message.info('Workflow сброшен. Начните заново.')
  }, [projectId, projectCrmId, form, clearTask, clearManualWorkflow, clearActiveAnalysis, resetDataLoaders, resetUpload, resetAnalysis, resetParsing, setSearchText, resetWorkflowKeepProject])

  // loadProjects, loadQueriesRegions, loadActiveCredentials, loadAIConfig
  // moved to useDataLoaders hook (Phase 2.2)

  // * Persist workflow state to localStorage (for both manual and yandex parsing)
  // NOTE: We intentionally DO NOT save large arrays (domains, parsing_result)
  // to avoid localStorage quota exceeded errors. These are restored from server.
  const persistManualWorkflowState = useCallback((step: number) => {
    if (!projectId) {
      return
    }

    // Only save for steps >= results (3) to avoid saving incomplete state
    if (step < STEP_INDEX.results) {
      return
    }

    console.log('💾 Saving workflow state at step', step, 'inputMethod:', inputMethod)

    try {
      saveManualWorkflow(projectId, {
        project_id: projectId,
        project_name: projectName || '',
        input_method: inputMethod || 'manual',
        current_step: step,
        task_id: taskId || '',
        // NOTE: domains and parsing_result are NOT saved to avoid quota exceeded
        ai_config: aiConfig,
        ai_filter: aiFilter,
        ai_search_text: aiSearchText,
        selected_phones: Array.from(selectedPhones),
      })
    } catch (error) {
      console.warn('Failed to persist workflow state (localStorage quota?):', error)
    }
  }, [projectId, projectName, inputMethod, taskId, aiConfig, aiFilter, aiSearchText, selectedPhones, saveManualWorkflow])


  // * Initialize modal
  const initializeModal = useCallback(async () => {
    console.log('🔧 Initializing modal...')

    // * Determine effective projectId (crm_id for API calls): use prop or find from localStorage
    let effectiveProjectId = projectCrmId || projectId

    // * If no projectId passed, try to find active analysis/workflow from localStorage
    if (!effectiveProjectId && !initialTaskId) {
      // Find any project with active analysis
      const parsingStore = useParsingStore.getState()
      const activeAnalysisEntries = Object.entries(parsingStore.activeAnalysisByProject)
      const runningAnalysis = activeAnalysisEntries.find(([, batch]) =>
        batch.status === 'running' || batch.status === 'pending'
      )
      if (runningAnalysis) {
        effectiveProjectId = Number(runningAnalysis[0])
        console.log(`🔍 Found active analysis for project ${effectiveProjectId} in localStorage`)
      }

      // Also check manual workflows if no active analysis
      if (!effectiveProjectId) {
        const workflowEntries = Object.entries(parsingStore.manualWorkflowByProject)
        if (workflowEntries.length > 0) {
          // Use the most recent workflow
          const sorted = workflowEntries.sort((a, b) =>
            new Date(b[1].saved_at).getTime() - new Date(a[1].saved_at).getTime()
          )
          effectiveProjectId = Number(sorted[0][0])
          console.log(`🔍 Found recent workflow for project ${effectiveProjectId} in localStorage`)
        }
      }
    }

    // * Phase 3: pre-fetch consolidated workflow state from new endpoint.
    // Result is exposed as `serverWorkflow` for consumers; legacy recovery
    // paths below remain as fallback. Failures are non-fatal.
    let serverWorkflowFresh: Awaited<ReturnType<typeof fetchServerWorkflow>> = null
    if (effectiveProjectId) {
      serverWorkflowFresh = await fetchServerWorkflow(effectiveProjectId)
    }

    // * Phase 4.1 early-skip: server is the source of truth.
    // * Если бэк говорит has_active_workflow=false — нет смысла дёргать
    // * getProjectLatestBatch и шерстить localStorage. Чистим stale-данные
    // * и идём в default-инициализацию.
    const hasLocalTrace = effectiveProjectId
      ? !!(
          getActiveAnalysis(effectiveProjectId) ||
          getManualWorkflow(effectiveProjectId) ||
          getActiveTask(effectiveProjectId)
        )
      : false
    const skipRecovery =
      !!effectiveProjectId &&
      !initialTaskId &&
      serverWorkflowFresh !== null &&
      !serverWorkflowFresh.has_active_workflow &&
      !hasLocalTrace

    if (skipRecovery && effectiveProjectId) {
      console.log('🟢 No active workflow on server, skipping recovery branches')
      // * Подметаем localStorage на случай древних следов (uploaded batches и т.п.)
      clearActiveAnalysis(effectiveProjectId)
      clearManualWorkflow(effectiveProjectId)
      clearTask(effectiveProjectId)
    }

    // Check for existing task in localStorage or on server
    if (effectiveProjectId && !initialTaskId && !skipRecovery) {
      // * FIRST: Check for active AI analysis batch on SERVER (cross-session sync)
      // This allows users with same account in different browsers to see active analysis
      try {
        console.log('🔍 Checking for active analysis batch on server...')
        const serverBatch = await api.getProjectLatestBatch(effectiveProjectId)

        if (serverBatch.has_batch && serverBatch.batch) {
          const batch = serverBatch.batch
          let isRunning = batch.status === 'running' || batch.status === 'pending'
          const isCompleted = batch.status === 'completed'

          // Check if running/pending batch is stale (created > 24 hours ago)
          // Stale batches are likely stuck and should not be restored
          if (isRunning && batch.started_at) {
            const startedAt = new Date(batch.started_at)
            const now = new Date()
            const hoursSinceStart = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60)
            if (hoursSinceStart > 24) {
              console.log(`⏭️ Skipping stale running batch #${batch.batch_id} (started ${hoursSinceStart.toFixed(0)} hours ago)`)
              // Clear stale localStorage data
              clearActiveAnalysis(effectiveProjectId)
              clearManualWorkflow(effectiveProjectId)
              clearTask(effectiveProjectId)
              isRunning = false // Treat as not running
            }
          }

          // Check if completed batch is recent (within last 60 minutes) - don't restore old batches
          let isRecentlyCompleted = false
          if (isCompleted && batch.completed_at) {
            const completedAt = new Date(batch.completed_at)
            const now = new Date()
            const minutesSinceCompletion = (now.getTime() - completedAt.getTime()) / (1000 * 60)
            isRecentlyCompleted = minutesSinceCompletion < 60 // Only restore if completed < 60 min ago
            if (!isRecentlyCompleted) {
              console.log(`⏭️ Skipping old completed batch #${batch.batch_id} (completed ${minutesSinceCompletion.toFixed(0)} min ago)`)
              // Clear stale localStorage data for old batches
              clearActiveAnalysis(effectiveProjectId)
              clearManualWorkflow(effectiveProjectId)
              clearTask(effectiveProjectId)
            }
          }

          // Restore if batch is running OR recently completed with results
          // For running batches, we want to restore even if results are empty (total_results > 0 means URLs exist)
          const hasResults = serverBatch.results && serverBatch.results.length > 0
          const hasTotalResults = serverBatch.total_results > 0
          // * Для completed-батчей восстанавливаем только если в localStorage есть след работы юзера
          // * (он сам запускал в этом браузере). Иначе чужой/старый батч "восстанавливается" сам
          // * при каждом открытии модалки — это спам и путаница.
          const hasLocalTrace = !!(
            getActiveAnalysis(effectiveProjectId) ||
            getManualWorkflow(effectiveProjectId) ||
            getActiveTask(effectiveProjectId)
          )
          const shouldRestore = isRunning || (isRecentlyCompleted && (hasResults || hasTotalResults) && hasLocalTrace)

          if (shouldRestore) {
            console.log(`🔄 Found batch on server: #${batch.batch_id} (${batch.status}), results: ${serverBatch.results?.length || 0}, total: ${serverBatch.total_results || batch.total}`)

            // * Load all results with pagination for large batches
            let allResults = serverBatch.results || []
            if (serverBatch.has_more) {
              console.log(`📥 Loading remaining results (${serverBatch.total_results} total)...`)
              let currentPage = 2
              while (true) {
                const nextPage = await api.getProjectLatestBatch(effectiveProjectId, { page: currentPage, pageSize: 500 })
                if (nextPage.results && nextPage.results.length > 0) {
                  allResults = [...allResults, ...nextPage.results]
                }
                if (!nextPage.has_more) break
                currentPage++
              }
            }

            // Restore domains from server results (optimized for large datasets)
            if (allResults.length > 0) {
              console.log(`📊 Restoring ${allResults.length} domains from server...`)

              // Process in chunks to avoid blocking UI
              const CHUNK_SIZE = 500
              const results = allResults
              const restoredDomains: DomainWithAnalysis[] = []

              const processChunk = (startIndex: number): Promise<void> => {
                return new Promise((resolve) => {
                  const endIndex = Math.min(startIndex + CHUNK_SIZE, results.length)

                  for (let i = startIndex; i < endIndex; i++) {
                    const r = results[i]
                    const fullUrl = r.url || ''
                    let hostname = r.domain || ''

                    try {
                      const urlObj = new URL(fullUrl.startsWith('http') ? fullUrl : `https://${fullUrl}`)
                      hostname = urlObj.hostname.replace(/^www\./, '')
                    } catch {
                      hostname = fullUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
                    }

                    // Determine source_type from URL path
                    let sourceType: 'domain' | 'url' = 'domain'
                    try {
                      const urlObj = new URL(fullUrl.startsWith('http') ? fullUrl : `https://${fullUrl}`)
                      if (urlObj.pathname && urlObj.pathname !== '/') {
                        sourceType = 'url'
                      }
                    } catch {
                      // Keep as domain
                    }

                    restoredDomains.push({
                      host: hostname,
                      full_url: fullUrl,
                      source_type: sourceType,
                      parsed_url_id: r.parsed_url_id,
                      source_id: r.source_id,
                      selected: r.ai_recommendation !== 'rejected',
                      ai_status: r.ai_analysis_status,
                      ai_score: r.ai_score,
                      ai_recommendation: r.ai_recommendation,
                      ai_reason: r.ai_reason,
                      analyzing: r.ai_analysis_status === 'analyzing',
                      // Required fields with defaults
                      total_clicks: r.total_clicks || 0,
                      queries_count: r.queries_count || 0,
                      average_position: r.average_position || 0,
                      popularity: r.popularity || 'parsed',
                    })
                  }

                  // Yield to UI thread
                  if (endIndex < results.length) {
                    setTimeout(() => processChunk(endIndex).then(resolve), 0)
                  } else {
                    resolve()
                  }
                })
              }

              await processChunk(0)
              setDomains(restoredDomains)
              console.log(`📊 Restored ${restoredDomains.length} domains from server`)
            }

            // For running batches, set up polling
            if (isRunning) {
              // Save batch ID for workflow step tracking
              setActiveBatchId(batch.batch_id)

              // Save to local store for future
              setActiveAnalysis(effectiveProjectId, {
                batch_id: batch.batch_id,
                project_id: effectiveProjectId,
                status: batch.status,
                total: batch.total,
                processed: batch.processed,
                started_at: batch.started_at || new Date().toISOString(),
              })

              // Go to AI analysis step while it's running
              setCurrentStep(STEP_INDEX.ai_analysis)
              setAnalyzing(true)
              message.info(`🔄 Восстановлен активный AI анализ (batch #${batch.batch_id}: ${batch.processed}/${batch.total})`)
              resumeAnalysisPoll(batch.batch_id)
              return // * IMPORTANT: prevent falling through to localStorage check which would create duplicate polling
            } else {
              // Save batch ID for workflow step tracking (completed batch)
              setActiveBatchId(batch.batch_id)

              // Completed batch - check workflow_step from database FIRST, then localStorage
              // workflow_step is the source of truth for which step user was on
              let targetStep: number = STEP_INDEX.ai_analysis

              // * Check workflow_step from database
              // If 'uploaded' - batch workflow is complete, don't restore (start fresh)
              if (batch.workflow_step === 'uploaded') {
                console.log(`📍 Batch #${batch.batch_id} already uploaded, starting fresh workflow`)
                // Clear stale localStorage data for this project
                clearActiveAnalysis(effectiveProjectId)
                clearManualWorkflow(effectiveProjectId)
                clearTask(effectiveProjectId)
                // Don't restore - let it fall through to normal initialization
              } else {
                // * PRIMARY: Use workflow_step from batch (database)
                if (batch.workflow_step) {
                  const stepMapping: Record<string, number> = {
                    'input': STEP_INDEX.method,
                    'parsing': STEP_INDEX.parsing,
                    'results': STEP_INDEX.results,
                    'ai_analysis': STEP_INDEX.ai_analysis,
                    'confirm': STEP_INDEX.confirm,
                  }
                  const dbStep = stepMapping[batch.workflow_step]
                  if (dbStep !== undefined && dbStep > STEP_INDEX.ai_analysis) {
                    targetStep = dbStep
                    console.log(`📍 Restoring to step ${targetStep} from DB workflow_step: ${batch.workflow_step}`)
                  }
                }
                // * FALLBACK: Check localStorage workflow
                else {
                  const storedWorkflow = getManualWorkflow(effectiveProjectId)
                  if (storedWorkflow && storedWorkflow.current_step > STEP_INDEX.ai_analysis) {
                    // User was past AI analysis, restore to their saved step
                    targetStep = storedWorkflow.current_step
                    console.log(`📍 Restoring to saved step ${targetStep} (from localStorage)`)
                  }
                }

                setCurrentStep(targetStep)
                setAnalyzing(false)
                console.log(`✅ Восстановлен завершённый AI анализ (batch #${batch.batch_id})`)
                return
              }
              // If 'uploaded' - fall through to normal initialization (fresh start)
            }
          }
        }
      } catch (error) {
        console.log('No active batch on server or error checking:', error)
      }

      // * Then check for active AI analysis batch in localStorage
      const activeAnalysis = getActiveAnalysis(effectiveProjectId)
      if (activeAnalysis && (activeAnalysis.status === 'running' || activeAnalysis.status === 'pending')) {
        console.log('🔄 Found active AI analysis batch in localStorage:', activeAnalysis.batch_id)

        // Verify batch is still running in backend
        try {
          const batchStatus = await api.getBatchStatus(activeAnalysis.batch_id)

          if (batchStatus.status === 'running' || batchStatus.status === 'pending') {
            // Restore minimal state from localStorage (domains restored from server)
            const storedManualWorkflow = getManualWorkflow(effectiveProjectId)
            if (storedManualWorkflow) {
              setTaskId(storedManualWorkflow.task_id)
              setAIConfig(storedManualWorkflow.ai_config || null)
              setAiFilter(storedManualWorkflow.ai_filter || 'all')
              setAiSearchText(storedManualWorkflow.ai_search_text || '')
              setSelectedPhones(new Set(storedManualWorkflow.selected_phones || []))
            }

            // Domains are restored from server in the main flow above
            setCurrentStep(STEP_INDEX.results)
            message.info(`🔄 Восстановлен AI анализ (batch #${activeAnalysis.batch_id})`)
            resumeAnalysisPoll(activeAnalysis.batch_id)
            return
          } else {
            // Batch is no longer running, clear it
            console.log('🔄 Batch is no longer running, clearing')
            clearActiveAnalysis(effectiveProjectId)
          }
        } catch (error) {
          console.error('Failed to verify batch status:', error)
          clearActiveAnalysis(effectiveProjectId)
        }
      }

      // * Then check for saved workflow (manual or yandex)
      const storedManualWorkflow = getManualWorkflow(effectiveProjectId)
      if (storedManualWorkflow) {
        console.log('🔄 Restoring workflow from localStorage', storedManualWorkflow)

        // Restore workflow state (domains NOT stored - restored from server)
        setInputMethod(storedManualWorkflow.input_method || 'manual')
        setTaskId(storedManualWorkflow.task_id)
        setAIConfig(storedManualWorkflow.ai_config || null)
        setAiFilter(storedManualWorkflow.ai_filter || 'all')
        setAiSearchText(storedManualWorkflow.ai_search_text || '')
        setSelectedPhones(new Set(storedManualWorkflow.selected_phones || []))

        // Try to restore domains from parsing result on server
        if (storedManualWorkflow.task_id) {
          try {
            const resultData = await api.getParsingResult(storedManualWorkflow.task_id)
            const transformedUrls = transformUrls(resultData.urls || [])
            setParsingResult({ ...resultData, urls: transformedUrls })
            const allForAnalysis = formatDomainsFromResult(resultData, transformedUrls)
            setDomains(allForAnalysis)
            setCurrentStep(storedManualWorkflow.current_step)
            message.info('Восстановлен workflow')
            return
          } catch (error) {
            console.warn('Failed to restore domains from server:', error)
            // Clear stale workflow and continue to normal initialization
            clearManualWorkflow(effectiveProjectId)
          }
        } else {
          // No task_id, clear stale workflow
          clearManualWorkflow(effectiveProjectId)
        }
      }

      const storedTask = getActiveTask(effectiveProjectId)
      if (storedTask) {
        // Skip already uploaded tasks
        if (storedTask.status === 'uploaded') {
          console.log('🔄 Task already uploaded, clearing and starting fresh')
          clearTask(effectiveProjectId)
        } else {
          // Verify task exists in backend
          try {
            await api.getParsingStatus(storedTask.task_id)

            setTaskId(storedTask.task_id)
            setProgress(storedTask.progress)
            setStatusMessage(storedTask.current_step || `Статус: ${storedTask.status}`)

            if (storedTask.status === 'running' || storedTask.status === 'pending') {
              setCurrentStep(STEP_INDEX.parsing)
              message.info('Восстановлен активный парсинг')
              pollStatus(storedTask.task_id)
              return
            } else if (storedTask.status === 'completed') {
              try {
                const resultData = await api.getParsingResult(storedTask.task_id)
                const transformedUrls = transformUrls(resultData.urls || [])

                setParsingResult({ ...resultData, urls: transformedUrls })

                const allForAnalysis = formatDomainsFromResult(resultData, transformedUrls)
                setDomains(allForAnalysis)

                // Use saved step from workflow if available and greater than results
                const savedWorkflow = getManualWorkflow(effectiveProjectId)
                const targetStep = (savedWorkflow && savedWorkflow.current_step > STEP_INDEX.results)
                  ? savedWorkflow.current_step
                  : STEP_INDEX.results

                setCurrentStep(targetStep)
                message.success('Восстановлены результаты парсинга')
                return
              } catch (error) {
                console.error('Failed to load results:', error)
                clearTask(effectiveProjectId)
              }
            }
          } catch {
            console.warn(`Phantom task detected and cleared: ${storedTask.task_id}`)
            clearTask(effectiveProjectId)
          }
        }
      }
    }

    // Load existing task if provided via props
    if (initialTaskId) {
      try {
        setTaskId(initialTaskId)
        const status = await api.getParsingStatus(initialTaskId)

        setProgress(status.progress || 0)
        setStatusMessage(status.message || `Статус: ${status.status}`)

        if (status.status === 'completed') {
          const resultData = await api.getParsingResult(initialTaskId)
          const transformedUrls = transformUrls(resultData.urls || [])

          setParsingResult({ ...resultData, urls: transformedUrls })

          const allForAnalysis = formatDomainsFromResult(resultData, transformedUrls)
          setDomains(allForAnalysis)

          setCurrentStep(STEP_INDEX.results)
        } else if (status.status === 'failed') {
          setParsingFailed(true)
          setStatusMessage(status.error || 'Парсинг завершился с ошибкой')
          setCurrentStep(STEP_INDEX.parsing)
        } else {
          setCurrentStep(STEP_INDEX.parsing)
          pollStatus(initialTaskId)
        }
        return
      } catch (error: any) {
        message.error('Ошибка загрузки задачи: ' + (error.message || 'Неизвестная ошибка'))
        setParsingFailed(true)
        setStatusMessage('Задача не найдена')
      }
    }

    // Default: Initialize method selection step
    setCurrentStep(STEP_INDEX.method)

    if (projectId && projectCrmId) {
      setSelectedCrmId(projectCrmId)
      form?.setFieldsValue({ project_id: projectId })
      loadQueriesRegions(projectCrmId)
    } else {
      loadProjects()
    }

    if (!credentialsId) {
      loadActiveCredentials()
    }
  }, [projectId, projectCrmId, initialTaskId, credentialsId, form, getActiveTask, getActiveAnalysis, getManualWorkflow, clearTask, clearManualWorkflow, clearActiveAnalysis, setActiveAnalysis, pollStatus, resumeAnalysisPoll, loadQueriesRegions, loadProjects, loadActiveCredentials, setAIConfig, setActiveBatchId, setAiFilter, setAiSearchText, setAnalyzing, setTaskId, setProgress, setStatusMessage, setParsingResult, setParsingFailed, fetchServerWorkflow, setCurrentStep, setInputMethod, setSelectedCrmId])

  // * Final cleanup guard: гарантированно очищаем оба polling-таймера
  // при размонтировании хука. Существующие cleanup'ы в resetState/handleStartOver
  // не покрывают случаи unmount без явного вызова (закрытие вкладки, navigate-away).
  // Этот эффект ДОЛЖЕН быть последним — без зависимостей, чтобы выполниться
  // только при unmount.
  useEffect(() => {
    return () => {
      stopParsingPoll()
      stopAnalysisPoll()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    // State
    inputMethod,
    setInputMethod,
    manualUrlsText,
    setManualUrlsText,
    manualSubsource,
    setManualSubsource,
    processingManualUrls,
    setProcessingManualUrls,
    downloadAll,
    setDownloadAll,
    minClicks,
    setMinClicks,
    topQueries,
    setTopQueries,
    currentStep,
    setCurrentStep,
    serverWorkflow,
    projects,
    loadingProjects,
    selectedCrmId,
    setSelectedCrmId,
    autoCredentialsId,
    queriesRegions,
    loadingQueriesRegions,
    resolvedRegions,
    selectedQueries,
    setSelectedQueries,
    selectedRegionCodes,
    setSelectedRegionCodes,
    taskId,
    setTaskId,
    progress,
    setProgress,
    statusMessage,
    setStatusMessage,
    parsingResult,
    setParsingResult,
    parsingFailed,
    setParsingFailed,
    domains,
    setDomains,
    aiConfig,
    setAIConfig,
    loadingAIConfig,
    analyzing,
    setAnalyzing,
    aiAnalysisSkipped,
    setAiAnalysisSkipped,
    batchProgress,
    resultsTab,
    setResultsTab,
    uploading,
    setUploading,
    uploadProgress,
    setUploadProgress,
    uploadResults,
    setUploadResults,
    localDbResults,
    setLocalDbResults,
    searchText,
    setSearchText,
    aiFilter,
    setAiFilter,
    aiSearchText,
    setAiSearchText,
    showAllDomains,
    setShowAllDomains,
    selectedPhones,
    setSelectedPhones,
    activeBatchId,
    setActiveBatchId,
    queueInfo,
    resumeAnalysisPoll,

    // Computed
    filteredDomains,
    selectedCount,

    // Store actions
    setActiveTask,
    updateTaskProgress,
    updateTaskStatus,
    completeTask,
    clearTask,
    getActiveTask,
    clearManualWorkflow,

    // Actions
    resetState,
    handleStartOver,
    loadProjects,
    loadQueriesRegions,
    loadActiveCredentials,
    loadAIConfig,
    toggleDomainSelection,
    removeDomain,
    saveSourceSelections,
    persistManualWorkflowState,
    initializeModal,
    handleStartParsing,
    handleAnalyze,
    handleUpload,
    pollStatus,

    // Props forwarding
    projectId,
    projectCrmId,
    projectName,
    credentialsId,
    initialTaskId,
    onClose,
  }
}

export type ParsingWorkflowState = ReturnType<typeof useParsingWorkflow>
